import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Flame, MapPin, Handshake } from "lucide-react";
import { BRAND } from "@/data/content";
import { Reveal, SectionLabel } from "@/components/site/Reveal";

const VALUES = [
  { icon: MapPin, title: "Nashville born & raised", text: "Proudly serving the community I call home." },
  { icon: Flame, title: "Firefighter & entrepreneur", text: "Showing up when people need me most — on call and online." },
  { icon: Handshake, title: "Relationships first", text: "Every project starts with trust, not a contract." },
];

const FounderPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A1428] text-white" data-testid="founder-page">
      {/* Sticky top bar */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0A1428]/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="founder-logo">
            <img src={BRAND.logo} alt={BRAND.name} className="h-10 w-10 rounded-lg object-contain" />
            <span className="font-display font-bold text-lg">TITAN<span className="text-[#1E6BFF]">.</span></span>
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-sm text-gray-300 hover:text-white transition-colors" data-testid="founder-back-home">
              <span className="inline-flex items-center gap-2"><ArrowLeft size={16} /> Home</span>
            </Link>
            <Link
              to="/#contact"
              data-testid="founder-plan-cta"
              className="hidden sm:inline-flex items-center rounded-full bg-[#1E6BFF] hover:bg-[#3B82F6] text-white text-sm font-semibold px-6 py-2.5 transition-colors duration-200 glow-blue"
            >
              Get a Free Plan
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-24 md:pb-36">
        {/* Header */}
        <div className="mb-14 md:mb-20">
          <SectionLabel testid="founder-label">Meet the Founder</SectionLabel>
          <Reveal>
            <h1
              data-testid="founder-heading"
              className="font-display font-black tracking-tighter text-white text-4xl md:text-6xl lg:text-7xl leading-[0.95] max-w-4xl"
            >
              Meet the Founder of <span className="text-[#1E6BFF]">Titan Web Solutions</span>
            </h1>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Portrait */}
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative rounded-3xl overflow-hidden border border-white/10">
                <img
                  src="/founder.jpg"
                  alt="Founder of Titan Web Solutions in Nashville Fire Department dress uniform"
                  data-testid="founder-photo"
                  className="w-full aspect-square object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1428]/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 rounded-xl backdrop-blur-xl bg-[#0A1428]/70 border border-white/10 p-5">
                  <p className="font-display font-semibold text-white">Founder, Titan Web Solutions</p>
                  <p className="text-sm text-gray-400 mt-1">Entrepreneur · Firefighter · Nashville, TN</p>
                </div>
              </div>
            </Reveal>

            {/* Values */}
            <div className="mt-8 space-y-4">
              {VALUES.map((v, i) => (
                <Reveal key={v.title} delay={0.1 + i * 0.05}>
                  <div
                    data-testid={`founder-value-${i}`}
                    className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5"
                  >
                    <span className="w-11 h-11 rounded-full bg-[#1E6BFF]/15 border border-[#1E6BFF]/30 flex items-center justify-center shrink-0">
                      <v.icon size={18} className="text-[#1E6BFF]" />
                    </span>
                    <span>
                      <span className="block text-white font-medium">{v.title}</span>
                      <span className="block text-sm text-gray-400 mt-0.5">{v.text}</span>
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Story */}
          <div className="lg:col-span-7" data-testid="founder-story">
            <Reveal delay={0.1}>
              <p className="text-gray-300 text-lg md:text-xl leading-relaxed">
                Born and raised in Nashville, Tennessee, I founded Titan Web Solutions with a vision
                to help businesses build a stronger presence online. As both an entrepreneur and a
                firefighter serving my community, I understand the importance of hard work,
                dedication, and showing up when people need you most.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 text-gray-400 text-lg leading-relaxed">
                My mission goes beyond building beautiful websites. I want to help small businesses,
                startups, and established companies stand out, connect with their customers, and turn
                their online presence into real opportunities for growth.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-gray-400 text-lg leading-relaxed">
                I believe every business deserves the chance to succeed, regardless of its size.
                That's why Titan Web Solutions is built around creating modern, professional, and
                engaging websites designed to help businesses move forward.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-6 text-gray-400 text-lg leading-relaxed">
                For me, this is about more than web design. It's about building relationships,
                supporting local businesses, and making a positive impact on the community I call
                home.
              </p>
            </Reveal>

            {/* Tagline */}
            <Reveal delay={0.3}>
              <div className="mt-12 rounded-3xl border border-[#1E6BFF]/30 bg-gradient-to-r from-[#1E6BFF]/10 to-transparent p-8 md:p-12">
                <p
                  data-testid="founder-tagline"
                  className="font-display font-bold tracking-tight text-white text-2xl md:text-4xl leading-tight"
                >
                  Titan Web Solutions — <span className="text-[#1E6BFF]">Where Creation Meets Reality.</span>
                </p>
                <Link
                  to="/#contact"
                  data-testid="founder-final-cta"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1E6BFF] hover:bg-[#3B82F6] text-white font-semibold px-8 py-4 transition-colors duration-200 glow-blue"
                >
                  Let's Build Together <ArrowUpRight size={18} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FounderPage;
